import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import DOMPurify from "dompurify";
import {
    Plus,
    FileSpreadsheet,
    FileText,
    Sparkles, Search
} from "lucide-react";
import { Tooltip, CircularProgress } from "@mui/material";
import ErrorHandler from "@Root/ErrorHandler";
import LabelCardForm from "@RegistriesForms/LabelCardForm";
import LabelCardViewService from "@Helpers/URLHelpers/LabelCardViewService";
import OptionsMenu from "@CommonRepresentationalDisplay/OptionsMenu";
import useDebounce from '@Hooks/useDebounce';
import { useInView } from 'react-intersection-observer';
import { useReduxProject } from "@Hooks/reduxProjectHook/useReduxProject";
import "./ContactsLabelCardPage.scss";

export default function ContactsLabelCardPage(props) {

    const [cursor, setCursor] = useState("auto");
    const [selectedItem, setSelectedItem] = useState(null);
    const [showOptionsMenu, setShowOptionsMenu] = useState(false);
    const [contextMouseX, setContextMouseX] = useState();
    const [contextMouseY, setContextMouseY] = useState();
    const [anchorOptionsMenu, setAnchorOptionsMenu] = useState(null);
    const [showLabelCardForm, setShowLabelCardForm] = useState(false);
    const [labelCardList, setLabelCardList] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [page, setPage] = useState(0);
    const [totalCardList, setTotalCardList] = useState(0);
    const [size] = useState(20);
    const debouncedSearch = useDebounce(searchQuery, 500, true);
    const { currentProjectId, getUserPermissionByProjectId } = useReduxProject();
    const defaultFilters = {
        labelCardFilter: {
            projectIdList: [currentProjectId],
            sortBy: "createTimeStamp",
            sortAsc: false,
            activeList: [true]
        },
        page: 0,
        size: 20
    };
    const [currentUserProjectPermissions, setCurrentUserProjectPermissions] = useState({});
    const [filters, setFilters] = useState(defaultFilters);
    const [ref, inView] = useInView({
        triggerOnce: false,
        rootMargin: '150px'
    });
    const isFetchingRef = useRef(false);
    const firstRenderRef = useRef(true);
    const errorHandler = new ErrorHandler();
    const labelCardViewService = new LabelCardViewService();
    const className = "contacts-labels";

    useEffect(() => {
        fetchLabels(0, true);
        setCurrentUserProjectPermissions(getUserPermissionByProjectId(currentProjectId));
    }, []);

    useEffect(() => {
        if (inView && hasMore && !isFetchingRef.current) {
            fetchLabels(page);
        }
    }, [inView, page, hasMore]);

    useEffect(() => {
        if (firstRenderRef.current) {
            firstRenderRef.current = false;
            return;
        }
        handleSearch();
    }, [debouncedSearch]);

    const handleSearch = async () => {
        setLoading(true);
        setLabelCardList([]);
        setPage(0);
        setHasMore(true);
        await fetchLabels(0, true);
        setLoading(false);
    };

    const fetchLabels = async (apiPage, isRefresh = false) => {
        if (isFetchingRef.current) return;
        isFetchingRef.current = true;
        if (!isRefresh) {
            setLoadingMore(true);
        }
        try {
            const filter = {
                ...defaultFilters,
                page: apiPage,
                size,
            };
            filter.labelCardFilter.searchQuery = debouncedSearch;
            let response = await labelCardViewService.getListFiltered(filter);
            if (response.status < 400) {
                response = JSON.parse(response.body);
                response = response.result;
                const list = response?.labelCardList;
                setTotalCardList(response.totalItems);
                const initialData = {
                    name: "All Contacts",
                    id: 0
                };
                setLabelCardList((prev) => {
                    if (isRefresh || apiPage === 0) {
                        return [initialData, ...list];
                    }

                    return [...prev, ...list];
                });
                const nextPage = apiPage + 1;
                setPage(nextPage);
                setHasMore(nextPage < response.totalPages);
            }
        } catch (err) {
            errorHandler.handleError(err);
        } finally {
            setLoadingMore(false);
            isFetchingRef.current = false;
        }
    };

    const resetAndFetch = async () => {
        setLoading(true);
        setLabelCardList([]);
        setPage(0);
        setHasMore(true);
        await fetchLabels(0, true);
        setLoading(false);
    };

    const toggleForm = () => {
        setShowLabelCardForm((prev) => !prev);
    };

    const handleSearchQueryChange = (e) => {
        const value = DOMPurify.sanitize(e.target.value);

        setSearchQuery(value);

        setFilters((prev) => ({
            ...prev,
            labelCardFilter: {
                ...prev.labelCardFilter,
                searchQuery: value
            }
        }));
    };

    const handleOptionsMenuPopup = (event, item) => {
        event?.preventDefault();

        if (!showOptionsMenu) {
            if (event) {
                setSelectedItem(item);
                setAnchorOptionsMenu(item);
                setContextMouseX(event.clientX);
                setContextMouseY(event.clientY);
                setShowOptionsMenu(true);
            }
        } else {
            setAnchorOptionsMenu(null);
            setShowOptionsMenu(false);
        }
    };

    const downloadTemplate = () => {
        const headers = [
            "Name*",
            "Email",
            "Area code",
            "Phone*",
            "Department",
            "Address",
            "State",
            "Label"
        ];

        const rows = [
            [
                "Rahul Joshi",
                "rahul.joshi@company.com",
                "91",
                "9876543210",
                "Tech Corp",
                "Bangalore",
                "Karnataka"
            ],
            [
                "Shreya Mishra",
                "shreya.mishra@example.com",
                "91",
                "9765432109",
                "Innovation LLC",
                "Mumbai",
                "Maharashtra"
            ],
            [
                "Karan Nair",
                "karan.nair@business.com",
                "91",
                "9654321098",
                "Global Solutions",
                "Delhi",
                "Delhi"
            ]
        ];

        const csv = [headers, ...rows]
            .map((row) =>
                row.map((cell) => `"${cell ?? ""}"`).join(",")
            )
            .join("\n");

        const blob = new Blob([csv], {
            type: "text/csv"
        });

        const url = window.URL.createObjectURL(blob);

        const a = document.createElement("a");

        a.href = url;
        a.download = "contacts_template.csv";

        a.click();

        window.URL.revokeObjectURL(url);
    };

    const reloadDeleteData = async () => {
        props?.setSelectedLabel({
            name: "All Contacts",
            id: 0
        });
        await resetAndFetch();
    };

    return (
        <>
            <aside
                className={className}
                style={{ cursor }}
            >


                <div className={`${className}__header`}>

                    <div>
                        <span className={`${className}__eyebrow`}>
                            CONTACTS
                        </span>

                        <h2>Lists</h2>
                    </div>

                    <Tooltip title="Create contact filter" arrow>
                        <button
                            type="button"
                            className={`${className}__add-btn`}
                            onClick={toggleForm}
                        >
                            <Plus size={15} />
                        </button>
                    </Tooltip>

                </div>

                <div className={`${className}__quick-actions`}>

                    <Link
                        to="import"
                        className={`${className}__quick-action`}
                    >
                        <span className={`${className}__quick-icon ${className}__quick-icon--green`}>
                            <FileSpreadsheet size={14} />
                        </span>

                        <span>
                            <strong>Import</strong>
                            <small>Excel file</small>
                        </span>
                    </Link>

                    <button
                        type="button"
                        className={`${className}__quick-action`}
                        onClick={downloadTemplate}
                    >
                        <span className={`${className}__quick-icon ${className}__quick-icon--blue`}>
                            <FileText size={14} />
                        </span>

                        <span>
                            <strong>Template</strong>
                            <small>Sample CSV</small>
                        </span>
                    </button>

                </div>

                <div className={`${className}__list-header`}>
					<div className={`${className}__search`}>
						<Search size={14} />
						<input
							type='text'
							value={searchQuery}
							onChange={(e) =>
								setSearchQuery(DOMPurify.sanitize(e.target.value))
							}
							placeholder='Search list...'
						/>
					</div>
                </div>

                <div className={`${className}__list`}>

                    {labelCardList.map((item) => {
                        const isActive = props?.selectedLabel?.id === item.id;
                        return (
                            <button
                                type="button"
                                key={item.id}
                                onContextMenu={(e) =>
                                    handleOptionsMenuPopup(e, item)
                                }
                                onClick={() =>
                                    props?.setSelectedLabel(item)
                                }
                                className={`
                                    ${className}__item
                                    ${isActive ? `${className}__item--active` : ""}
                                `}
                            >

                                <span className={`${className}__item-dot`} />

                                <span className={`${className}__item-name`}>
                                    {item?.name}
                                </span>
                                {item?.id !== 0 ?
                                    <span className={`${className}__list-count`}>
                                        {item?.registryLinkCount}
                                    </span>
                                :
                                    <Sparkles
                                        size={13}
                                        className={`${className}__item-star`}
                                    />
                                }
                            </button>
                        );
                    })}
                    {hasMore && (
                        <div
                            ref={ref}
                            className="campaigns-load-more"
                        >
                            {loadingMore && (
                                <>
                                    <CircularProgress
                                        size={20}
                                    />

                                    <span>
                                        Loading more filters...
                                    </span>
                                </>
                            )}
                        </div>
                    )}

                </div>

            </aside>


            {showLabelCardForm && (
                <LabelCardForm
                    open={showLabelCardForm}
                    closePopup={toggleForm}
                    reloadData={resetAndFetch}
                />
            )}


            {!!selectedItem && (
                <OptionsMenu
                    open={showOptionsMenu}
                    closePopup={handleOptionsMenuPopup}
                    showView={false}
                    showDuplicate={false}
                    showUpdate={true}
                    showCreate={false}
                    type="label"
                    handleOpenView={false}
                    anchorEl={anchorOptionsMenu}
                    anchorPosition={{
                        top: contextMouseY,
                        left: contextMouseX
                    }}
                    renderData={resetAndFetch}
                    reloadData={reloadDeleteData}
                    labelCardList={labelCardList}
                    item={selectedItem}
                />
            )}

        </>
    );
}
